// hooks/useCVAnalysis.js
import { useState, useCallback } from 'react';
import { supabase } from "../supabase";
import { extractTextFromFile } from "../utils/pdfConversion";
import { removePIIDetailed } from "../utils/piiRemover";

// The API endpoint for CV analysis
const CV_ANALYSIS_ENDPOINT = "https://zloptqlrfovwujzgytlm.supabase.co/functions/v1/llama-chat";

/**
 * Hook for handling CV analysis functionality
 * @param {Object} user - The authenticated user object
 * @returns {Object} - CV analysis methods and state
 */
const useCVAnalysis = (user) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [analysisResult, setAnalysisResult] = useState(null);
  
  /**
   * Reset the state of the hook
   */
  const resetState = useCallback(() => {
    setLoading(false);
    setError(null);
    setStatusMessage("");
    setAnalysisResult(null);
  }, []);

  /**
   * Submit a CV for analysis
   * @param {Object} params - Parameters for CV analysis
   * @param {File} params.file - The CV file (PDF, DOCX)
   * @param {string} params.jobCategory - The job category ID
   * @param {string} params.country - The country for analysis context
   * @param {Object} params.dependencies - Optional dependencies (like mammoth)
   * @returns {Promise<Object>} - A promise that resolves to the analysis request ID
   */
  const submitCV = useCallback(async ({ file, jobCategory, country = "South Africa", dependencies = {} }) => {
    if (!user) {
      setError("You must be logged in to submit a CV for analysis");
      return null;
    }
    
    let requestId = null;
    
    try {
      resetState();
      setLoading(true);
      
      // Enhanced file validation
      console.log("File being processed:", {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });
      
      // Step 1: Create a CV analysis request record
      setStatusMessage("Creating analysis request...");
      const { data: requestData, error: requestError } = await supabase
        .from('cv_analysis_requests')
        .insert({
          user_id: user.id,
          file_name: file.name,
          job_category: jobCategory,
          status: 'processing'
        })
        .select()
        .single();
        
      if (requestError) {
        console.error("Request creation error:", requestError);
        throw new Error(`Failed to create analysis request: ${requestError.message}`);
      }
      
      requestId = requestData.id;
      console.log("Created request with ID:", requestId);
      
      // Step 2: Extract text from the file
      setStatusMessage("Extracting text from document...");
      console.log("Starting text extraction from file");
      let extractedText;
      try {
        extractedText = await extractTextFromFile(file, dependencies);
        console.log("Text extracted successfully, length:", extractedText.length);
        
        if (!extractedText || extractedText.trim().length === 0) {
          throw new Error("No text could be extracted from the file. Please ensure your document contains readable text.");
        }
      } catch (extractError) {
        console.error("Text extraction error:", extractError);
        throw new Error(`Failed to extract text from file: ${extractError.message}`);
      }
      
      // Step 2.5: Remove PII from extracted text
      setStatusMessage("Removing sensitive information (PII) from document...");
      console.log("Starting PII removal from extracted text");
      let cleanedText;
      let piiRemovalResults;
      try {
        piiRemovalResults = removePIIDetailed(extractedText, {
          email: { remove: true },
          phone: { remove: true },
          saId: { remove: true },           
          ssn: { remove: true },
          creditCard: { remove: true },
          passport: { remove: true },
          medicalId: { remove: true },      
          driversLicense: { remove: true },
          address: { remove: true },
          dateOfBirth: { remove: true },
          ipAddress: { remove: true },
          url: { remove: false }            
        });
        
        cleanedText = piiRemovalResults.cleanedText;
        console.log("PII removal completed:", {
          originalLength: piiRemovalResults.originalLength,
          cleanedLength: piiRemovalResults.cleanedLength,
          reductionPercentage: piiRemovalResults.reductionPercentage,
          removedItemsCount: piiRemovalResults.removedItems.length,
          removedTypes: piiRemovalResults.removedItems.map(item => item.type)
        });
        
        if (piiRemovalResults.removedItems.length > 0) {
          console.log("PII items removed:", piiRemovalResults.removedItems);
        }
        
      } catch (piiError) {
        console.error("PII removal error:", piiError);
        // If PII removal fails, use original text and log the error
        cleanedText = extractedText;
        console.warn("PII removal failed, proceeding with original text");
      }
      
      // Step 3: Send text for CV analysis with updated API format
      setStatusMessage("Analyzing CV with Dr. Jobs methodology...");
      console.log("Sending request to analysis API with category:", jobCategory, "and country:", country);
      console.log("CV text preview (PII-cleaned):", cleanedText.substring(0, 400));
      const response = await fetch(CV_ANALYSIS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inpsb3B0cWxyZm92d3Vqemd5dGxtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQyNzU5NTAsImV4cCI6MjA1OTg1MTk1MH0.0rjBqtIoohOO7qY4Bim_kCI18hsL0AR_UjSRUXyyxNI'
        },
        body: JSON.stringify({
          cvText: cleanedText,
          jobCategory: jobCategory,
          country: country
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API response error:", response.status, errorText);
        throw new Error(`Analysis API error (${response.status}): ${errorText}`);
      }
      
      const analysisResult = await response.json();
      console.log("Analysis completed successfully");
      
      // Step 4: Store analysis results
      setStatusMessage("Saving analysis results...");
      console.log("Storing analysis results for request:", requestId);
      
      try {
        await storeAnalysisResults(requestId, analysisResult);
        console.log("Analysis results stored successfully");
      } catch (storeError) {
        console.error("Error storing results:", storeError);
        throw new Error(`Failed to store analysis results: ${storeError.message}`);
      }
      
      // Step 5: Update the request status to "completed"
      setStatusMessage("Finalizing analysis...");
      console.log("Updating request status to completed for request:", requestId);
      
      try {
        const { error: updateError } = await supabase
          .from('cv_analysis_requests')
          .update({ 
            status: 'completed',
            updated_at: new Date().toISOString()
          })
          .eq('id', requestId);
          
        if (updateError) {
          console.error("Status update error:", updateError);
          console.warn("Analysis completed but status update failed:", updateError.message);
        } else {
          console.log("Status successfully updated to completed");
        }
      } catch (statusUpdateError) {
        console.error("Error during status update process:", statusUpdateError);
      }
      
      setAnalysisResult({ requestId, success: true });
      
      return {
        requestId,
        success: true
      };
      
    } catch (err) {
      console.error("Error processing CV:", err);
      setError(err.message || "Failed to process CV");
      
      // Update request status to 'failed' if we have a requestId
      if (requestId) {
        console.log("Updating failed request status for:", requestId);
        try {
          const { error: failedUpdateError } = await supabase
            .from('cv_analysis_requests')
            .update({ 
              status: 'failed',
              updated_at: new Date().toISOString()
            })
            .eq('id', requestId);
            
          if (failedUpdateError) {
            console.error("Failed to update request status to failed:", failedUpdateError);
          }
        } catch (updateError) {
          console.error("Error updating failed status:", updateError);
        }
      }
      
      return null;
    } finally {
      setLoading(false);
      setStatusMessage("");
    }
  }, [user, resetState]);

/**
 * Helper function to safely convert values to appropriate database types
 */
const safeConvert = {
  toInt: (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    const num = Number(value);
    return isNaN(num) ? defaultValue : Math.round(num);
  },
  toString: (value, defaultValue = '') => {
    if (value === null || value === undefined) return defaultValue;
    return String(value);
  },
  toDecimal: (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    const num = Number(value);
    return isNaN(num) ? defaultValue : Math.round(num * 100) / 100; // Round to 2 decimal places
  }
};

/**
 * Store CV analysis results in the database (Updated for new API format and schema)
 * @param {string} requestId - The CV analysis request ID
 * @param {Object} analysisResult - The analysis result from the API
 * @returns {Promise<void>}
 */
const storeAnalysisResults = async (requestId, analysisResult) => {
  try {
    const { result } = analysisResult;
    
    // Handle new Dr. Jobs format
    if (result && result.category && result.assessmentReport) {
      // New format with updated Dr. Jobs structure
      const { 
        category,
        country,
        assessmentReport: {
          overallScore,
          categoryScores,
          overallFeedback,
          detailedFeedback,
          recommendations
        }
      } = result;

      console.log("Storing category scores:", categoryScores);
      
      // Convert categoryScores object to array format for storage
      const categoryScoresArray = Object.entries(categoryScores || {}).map(([name, data]) => ({
        category: name,
        numerator: data.score,
        denominator: data.outOf,
        percentage: parseFloat(data.percentage.replace('%', ''))
      }));
      
      // Ensure we have 5 categories with default values if missing
      const paddedScores = [];
      for (let i = 0; i < 5; i++) {
        paddedScores[i] = categoryScoresArray[i] || {
          category: `Category ${i + 1}`,
          numerator: 0,
          denominator: [26, 52, 20, 32, 16][i], // New default max values
          percentage: 0
        };
      }
      
      // Handle the new recommendation structure
      const strengtheningApplication = recommendations?.strengtheningTheApplicationItself || [];
      const professionalDevelopment = recommendations?.professionalDevelopmentAndUpskilling || [];
      
      // Extract strengths and areas for improvement as separate fields
      const strengthsArray = detailedFeedback?.strengths || [];
      const improvementsArray = detailedFeedback?.areasForImprovement || [];
      
      // Convert arrays to formatted text
      const strengthsText = strengthsArray.length > 0 
        ? strengthsArray.join('\n• ') 
        : '';
      const improvementsText = improvementsArray.length > 0 
        ? improvementsArray.join('\n• ') 
        : '';
      
      // Combined detailed feedback for backward compatibility
      const combinedDetailedFeedback = [
        strengthsText ? `Strengths:\n• ${strengthsText}` : '',
        improvementsText ? `Areas for Improvement:\n• ${improvementsText}` : ''
      ].filter(Boolean).join('\n\n');
      
      // Extract overall percentage
      const overallPercentage = overallScore?.percentage 
        ? parseFloat(overallScore.percentage.replace('%', ''))
        : null;
      
      // Store the analysis results with proper data type conversion
      const insertData = {
        request_id: requestId,
        
        // Overall score - handle new format with score/outOf structure
        overall_score: safeConvert.toInt(overallScore?.score),
        overall_max_score: safeConvert.toInt(overallScore?.outOf),
        overall_percentage: safeConvert.toDecimal(overallPercentage),
        
        // Category 1 - Foundational Qualifications & Compliance
        category_1_name: safeConvert.toString(paddedScores[0].category),
        category_1_score: safeConvert.toInt(paddedScores[0].numerator),
        category_1_max: safeConvert.toInt(paddedScores[0].denominator),
        category_1_percentage: safeConvert.toDecimal(paddedScores[0].percentage),
        
        // Category 2 - Professional Skills & Attributes
        category_2_name: safeConvert.toString(paddedScores[1].category),
        category_2_score: safeConvert.toInt(paddedScores[1].numerator),
        category_2_max: safeConvert.toInt(paddedScores[1].denominator),
        category_2_percentage: safeConvert.toDecimal(paddedScores[1].percentage),
        
        // Category 3 - Professional Development & Engagement
        category_3_name: safeConvert.toString(paddedScores[2].category),
        category_3_score: safeConvert.toInt(paddedScores[2].numerator),
        category_3_max: safeConvert.toInt(paddedScores[2].denominator),
        category_3_percentage: safeConvert.toDecimal(paddedScores[2].percentage),
        
        // Category 4 - Academic & Scholarly Contributions
        category_4_name: safeConvert.toString(paddedScores[3].category),
        category_4_score: safeConvert.toInt(paddedScores[3].numerator),
        category_4_max: safeConvert.toInt(paddedScores[3].denominator),
        category_4_percentage: safeConvert.toDecimal(paddedScores[3].percentage),
        
        // Category 5 - Application Quality & Presentation
        category_5_name: safeConvert.toString(paddedScores[4].category),
        category_5_score: safeConvert.toInt(paddedScores[4].numerator),
        category_5_max: safeConvert.toInt(paddedScores[4].denominator),
        category_5_percentage: safeConvert.toDecimal(paddedScores[4].percentage),
        
        // Job category, country, and feedback
        job_category: safeConvert.toString(category, 'Unknown'),
        country: safeConvert.toString(country, 'ZA'),
        detailed_feedback: safeConvert.toString(combinedDetailedFeedback || overallFeedback),
        assessment_report: safeConvert.toString(overallFeedback),
        
        // New separate feedback fields
        strengths: strengthsText ? `• ${strengthsText}` : null,
        areas_for_improvement: improvementsText ? `• ${improvementsText}` : null,
        
        // Recommendations as JSON strings
        strengthening_application: JSON.stringify(strengtheningApplication),
        professional_development: JSON.stringify(professionalDevelopment),
        
        // Store full result as JSON for reference
        full_analysis: JSON.stringify(result),
        
        // Set timestamps
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        
        // Legacy fields (set to null for new format)
        recommendations: null
      };

      console.log("Insert data prepared:", {
        ...insertData,
        full_analysis: '[JSON data]' // Don't log the full JSON
      });

      const { error } = await supabase
        .from('cv_analysis_results')
        .insert(insertData);
        
      if (error) {
        console.error("Database insert error:", error);
        throw error;
      }
      
    } else if (result) {
      // Fallback for different format or malformed data
      console.warn('Unexpected analysis result format, using fallback storage');
      
      // Create a fallback analysis with updated default values
      const { error } = await supabase
        .from('cv_analysis_results')
        .insert({
          request_id: requestId,
          overall_score: safeConvert.toInt(result.overallScore, 110),
          overall_max_score: 146,
          overall_percentage: 75.3,
          
          // Updated default category scores with new max values
          category_1_name: 'Foundational Qualifications & Compliance',
          category_1_score: 20,
          category_1_max: 26,
          category_1_percentage: 76.9,
          
          category_2_name: 'Professional Skills & Attributes',
          category_2_score: 40,
          category_2_max: 52,
          category_2_percentage: 76.9,
          
          category_3_name: 'Professional Development & Engagement',
          category_3_score: 15,
          category_3_max: 20,
          category_3_percentage: 75.0,
          
          category_4_name: 'Academic & Scholarly Contributions',
          category_4_score: 20,
          category_4_max: 32,
          category_4_percentage: 62.5,
          
          category_5_name: 'Application Quality & Presentation',
          category_5_score: 15,
          category_5_max: 16,
          category_5_percentage: 93.8,
          
          job_category: safeConvert.toString(result.category, 'Unknown'),
          country: 'ZA',
          detailed_feedback: safeConvert.toString(result.detailedFeedback, 'Auto-generated feedback due to analysis error.'),
          assessment_report: safeConvert.toString(result.detailedFeedback, 'Auto-generated feedback due to analysis error.'),
          
          strengths: '• Auto-generated strength 1\n• Auto-generated strength 2',
          areas_for_improvement: '• Auto-generated improvement 1\n• Auto-generated improvement 2',
          
          strengthening_application: JSON.stringify(['Auto-generated recommendation 1', 'Auto-generated recommendation 2']),
          professional_development: JSON.stringify(['Auto-generated development 1', 'Auto-generated development 2']),
          
          full_analysis: JSON.stringify(result),
          
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          recommendations: null
        });
        
      if (error) {
        console.error("Fallback database insert error:", error);
        throw error;
      }
    } else {
      throw new Error('Invalid analysis result format');
    }
  } catch (error) {
    console.error("Error storing analysis results:", error);
    throw new Error(`Failed to store analysis results: ${error.message}`);
  }
};

  /**
   * Fetch a CV analysis result by ID
   * @param {string} analysisId - The CV analysis request ID
   * @returns {Promise<Object>} - A promise that resolves to the analysis result
   */
  const fetchAnalysisById = useCallback(async (analysisId) => {
    if (!user) {
      setError("You must be logged in to view analysis results");
      return null;
    }
    
    try {
      setLoading(true);
      
      // Fetch the analysis request
      const { data: requestData, error: requestError } = await supabase
        .from('cv_analysis_requests')
        .select('*')
        .eq('id', analysisId)
        .single();
        
      if (requestError) throw requestError;
      
      if (requestData.user_id !== user.id) {
        throw new Error("You don't have permission to view these results");
      }
      
      // Fetch the analysis results
      const { data: resultData, error: resultError } = await supabase
        .from('cv_analysis_results')
        .select('*')
        .eq('request_id', analysisId)
        .single();
        
      if (resultError) {
        // If no results yet, check request status
        if (resultError.code === 'PGRST116') {
          if (requestData.status === 'processing') {
            return {
              requestDetails: requestData,
              status: 'processing',
              message: "Your CV is still being analyzed with Dr. Jobs methodology. Please check back in a few minutes."
            };
          } else if (requestData.status === 'failed') {
            return {
              requestDetails: requestData,
              status: 'failed',
              message: "We encountered an issue analyzing your CV. Please try uploading it again."
            };
          } else {
            return {
              requestDetails: requestData,
              status: 'pending',
              message: "No analysis results found for this request."
            };
          }
        } else {
          throw resultError;
        }
      }
      
      return {
        requestDetails: requestData,
        result: resultData,
        status: 'completed'
      };
    } catch (err) {
      console.error("Error fetching analysis:", err);
      setError(err.message || "Failed to fetch analysis results");
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetch all CV analyses for the current user
   * @returns {Promise<Array>} - A promise that resolves to an array of analyses
   */
  const fetchUserAnalyses = useCallback(async () => {
    if (!user) {
      setError("You must be logged in to view analyses");
      return [];
    }
    
    try {
      setLoading(true);
      
      // Get all analyses for the user
      const { data, error } = await supabase
        .from('cv_analysis_requests')
        .select('*, cv_analysis_results(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return data || [];
    } catch (err) {
      console.error("Error fetching analyses:", err);
      setError(err.message || "Failed to fetch analysis history");
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Get the latest CV analysis result for the current user
   * @returns {Promise<Object|null>} - A promise that resolves to the latest analysis result or null
   */
  const getLatestAnalysis = useCallback(async () => {
    if (!user) {
      return null;
    }
    
    try {
      const { data, error } = await supabase
        .from('cv_analysis_requests')
        .select('*, cv_analysis_results(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error("Error fetching latest analysis:", error);
        return null;
      }

      return data;
    } catch (err) {
      console.error("Error in getLatestAnalysis:", err);
      return null;
    }
  }, [user]);

  return {
    // State
    loading,
    error,
    statusMessage,
    analysisResult,
    
    // Methods
    submitCV,
    fetchAnalysisById,
    fetchUserAnalyses,
    getLatestAnalysis,
    resetState
  };
};

export default useCVAnalysis;